import React, { useEffect, useState } from "react";
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Container,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Menu,
  MenuItem,
  Alert,
  Snackbar,
  useMediaQuery,
  ListItemText,
  ListItem,
  List,
  Drawer,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import BusinessIcon from "@mui/icons-material/Business";
import MenuIcon from "@mui/icons-material/Menu";
import PolicyIcon from "@mui/icons-material/Policy";
import LanguageIcon from "@mui/icons-material/Language";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SelfImprovementIcon from "@mui/icons-material/SelfImprovement";
import DirectionsRunIcon from "@mui/icons-material/DirectionsRun";
import SchoolIcon from "@mui/icons-material/School";

// Assets
import DynamicDeskLogo from "../assets/DD_2_Weiss.png";

// Header and Footer Components
import AboutUs from "../headers/AboutUs";
import HelpFAQPage from "../headers/HelpFAQPage";
import PrivacyPolicyPage from "../footers/PrivacyPolicyPage";
import TermsPage from "../footers/TermsPage";
import CookiePolicyPage from "../footers/CookiePolicyPage";
import ImprintPage from "../footers/ImprintPage";
import AdditionalTermsPage from "../footers/AdditionalTermsPage";
import FeedbackComponent from "../feedback/FeedbackComponent";

// Services
import DownloadService from "../services/firebase/DownloadService";

// Utils
import { isElectron } from "../util/ElectronDetection";
import { useAuthStore } from "../stores/zustand";

/**
 * LandingPage Component
 *
 * Initial landing page with header, footer, and product selection in the middle
 */
const LandingPage = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  // State for menu management
  const [languageMenuAnchor, setLanguageMenuAnchor] = useState(null);
  const [footerMenuAnchor, setFooterMenuAnchor] = useState(null);
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState(null);

  // State for showing different pages
  const [currentPage, setCurrentPage] = useState("home");

  // State for download functionality
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(null);
  const [downloadSuccess, setDownloadSuccess] = useState(false);

  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const isMobile = useMediaQuery("(max-width:700px)");

  const handleCorporateLogin = () => {
    navigate("/corporate");
  };

  const handlePhysioLogin = () => {
    navigate("/physio");
  };

  const handlePhysioUserLogin = () => {
    navigate("/physio-user");
  };

  const handleCourseLogin = () => {
    window.location.href = "https://vielsitzerkurs.web.app";
  };

  const handleLanguageChange = (language) => {
    i18n.changeLanguage(language);
    setLanguageMenuAnchor(null);
  };

  // Download functionality
  const handleDownload = async (platform = null) => {
    try {
      setIsDownloading(true);
      setDownloadError(null);
      setDownloadMenuAnchor(null); // Close the menu

      const result = await DownloadService.downloadInstaller(platform);

      if (result.success) {
        setDownloadSuccess(true);
      }
    } catch (error) {
      console.error("Download failed:", error);
      setDownloadError(error.message || "Download failed. Please try again.");
    } finally {
      setIsDownloading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setDownloadError(null);
    setDownloadSuccess(false);
  };

  // Get available platforms for dropdown
  const availablePlatforms = DownloadService.getAvailablePlatforms();

  // Helper function to detect mobile devices
  const isMobileDevice = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    return (
      /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
        userAgent
      ) || window.innerWidth <= 768
    ); // Also consider screen width
  };

  // Determine if download button should be shown
  const shouldShowDownloadButton = () => {
    return !isElectron() && !isMobileDevice();
  };

  //Redirect authenticated users away from login page
  useEffect(() => {
    const { isAuthenticated, isEmailVerified } = useAuthStore.getState();
    if (isAuthenticated() && isEmailVerified) {
      navigate("/home", { replace: true }); // Redirect to home if already authenticated
    }
  }, [navigate]);

  const renderCurrentPage = () => {
    switch (currentPage) {
      case "about":
        return (
          <Box sx={{ position: "relative", py: 12 }}>
            <AboutUs />
          </Box>
        );
      case "help":
        return (
          <Box sx={{ position: "relative", py: 12 }}>
            <HelpFAQPage />
          </Box>
        );
      case "privacy":
        return (
          <Box sx={{ position: "relative", py: 12 }}>
            <PrivacyPolicyPage />
          </Box>
        );
      case "terms":
        return (
          <Box sx={{ position: "relative", py: 12 }}>
            <TermsPage />
          </Box>
        );
      case "cookies":
        return (
          <Box sx={{ position: "relative", py: 12 }}>
            <CookiePolicyPage />
          </Box>
        );
      case "imprint":
        return (
          <Box sx={{ position: "relative", py: 12 }}>
            <ImprintPage />
          </Box>
        );
      case "additional-terms":
        return (
          <Box sx={{ position: "relative", py: 12 }}>
            <AdditionalTermsPage />
          </Box>
        );
      case "feedback":
        return (
          <Box sx={{ position: "relative", py: 12 }}>
            <FeedbackComponent />
          </Box>
        );
      default:
        return (
          <Container maxWidth="md" sx={{ py: 12 }}>
            {/* Title */}
            <Box sx={{ textAlign: "center", mb: 6 }}>
              <Typography
                variant="h3"
                gutterBottom
                sx={{ color: "white", fontWeight: "bold" }}
              >
                {t("landingPage.welcome")}
              </Typography>
              <Typography
                variant="h6"
                sx={{ color: "rgba(255, 255, 255, 0.8)", mb: 4 }}
              >
                {t("landingPage.chooseLoginType")}
              </Typography>
            </Box>

            {/* Product Selection Cards */}
            <Grid
              container
              spacing={4}
              justifyContent="center"
              // sx={{ maxWidth: "1200px", margin: "0 auto" }}
            >
              {/* Corporate Login Card */}
              <Grid item xs={12} sm={6}>
                <Card
                  sx={{
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                    borderRadius: 2,
                    transition: "all 0.3s ease",
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.15)",
                      transform: "translateY(-4px)",
                      boxShadow: "0 8px 25px rgba(0, 0, 0, 0.3)",
                    },
                  }}
                  onClick={handleCorporateLogin}
                >
                  <CardContent sx={{ textAlign: "center", py: 4 }}>
                    <BusinessIcon
                      sx={{
                        fontSize: 60,
                        color: "#063bd0",
                        mb: 2,
                      }}
                    />
                    <Typography
                      variant="h5"
                      gutterBottom
                      sx={{ color: "white", fontWeight: "bold" }}
                    >
                      {t("landingPage.corporateLogin")}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ color: "rgba(255, 255, 255, 0.8)" }}
                    >
                      {t("landingPage.corporateDescription")}
                    </Typography>
                  </CardContent>
                  <CardActions sx={{ justifyContent: "center", pb: 3 }}>
                    <Button
                      variant="contained"
                      size="large"
                      sx={{
                        backgroundColor: "#063bd0",
                        color: "white",
                        px: 4,
                        py: 1.5,
                        fontSize: "1.1rem",
                        fontWeight: "bold",
                        "&:hover": {
                          backgroundColor: "#052ca8",
                        },
                      }}
                    >
                      {t("landingPage.continueAsCorporate")}
                    </Button>
                  </CardActions>
                </Card>
              </Grid>

              {/* Preventive Course Login Card */}
              <Grid item xs={12} sm={6}>
                <Card
                  sx={{
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                    borderRadius: 2,
                    transition: "all 0.3s ease",
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.15)",
                      transform: "translateY(-4px)",
                      boxShadow: "0 8px 25px rgba(0, 0, 0, 0.3)",
                    },
                  }}
                  onClick={handleCourseLogin}
                >
                  <CardContent sx={{ textAlign: "center", py: 4 }}>
                    <SchoolIcon
                      sx={{
                        fontSize: 60,
                        color: "#05b67f",
                        mb: 2,
                      }}
                    />
                    <Typography
                      variant="h5"
                      gutterBottom
                      sx={{ color: "white", fontWeight: "bold" }}
                    >
                      {t("landingPage.preventiveCourseLogin")}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ color: "rgba(255, 255, 255, 0.8)" }}
                    >
                      {t("landingPage.preventiveCourseDescription")}
                    </Typography>
                  </CardContent>
                  <CardActions sx={{ justifyContent: "center", pb: 3 }}>
                    <Button
                      variant="contained"
                      size="large"
                      sx={{
                        backgroundColor: "#05b67f",
                        color: "white",
                        px: 4,
                        py: 1.5,
                        fontSize: "1.1rem",
                        fontWeight: "bold",
                        "&:hover": {
                          backgroundColor: "#048a66",
                        },
                      }}
                    >
                      {t("landingPage.continueAsCourse")}
                    </Button>
                  </CardActions>
                </Card>
              </Grid>

              {/* Physio Login Card */}
              <Grid item xs={12} sm={6}>
                <Card
                  sx={{
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                    borderRadius: 2,
                    transition: "all 0.3s ease",
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.15)",
                      transform: "translateY(-4px)",
                      boxShadow: "0 8px 25px rgba(0, 0, 0, 0.3)",
                    },
                  }}
                  onClick={handlePhysioLogin}
                >
                  <CardContent sx={{ textAlign: "center", py: 4 }}>
                    <SelfImprovementIcon
                      sx={{
                        fontSize: 60,
                        color: "#db5d09ff",
                        mb: 2,
                      }}
                    />
                    <Typography
                      variant="h5"
                      gutterBottom
                      sx={{ color: "white", fontWeight: "bold" }}
                    >
                      {t("landingPage.physiotherapistLogin")}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ color: "rgba(255, 255, 255, 0.8)" }}
                    >
                      {t("landingPage.physiotherapistDescription")}
                    </Typography>
                  </CardContent>
                  <CardActions sx={{ justifyContent: "center", pb: 3 }}>
                    <Button
                      variant="contained"
                      size="large"
                      sx={{
                        backgroundColor: "#db5d09ff",
                        color: "white",
                        px: 4,
                        py: 1.5,
                        fontSize: "1.1rem",
                        fontWeight: "bold",
                        "&:hover": {
                          backgroundColor: "#aa4705ff",
                        },
                      }}
                    >
                      {t("landingPage.continueAsPhysiotherapist")}
                    </Button>
                  </CardActions>
                </Card>
              </Grid>

              {/* Physio User Login Card */}
              <Grid item xs={12} sm={6}>
                <Card
                  sx={{
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                    borderRadius: 2,
                    transition: "all 0.3s ease",
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.15)",
                      transform: "translateY(-4px)",
                      boxShadow: "0 8px 25px rgba(0, 0, 0, 0.3)",
                    },
                  }}
                  onClick={handlePhysioUserLogin}
                >
                  <CardContent sx={{ textAlign: "center", py: 4 }}>
                    <DirectionsRunIcon
                      sx={{
                        fontSize: 60,
                        color: "#F59E0B",
                        mb: 2,
                      }}
                    />
                    <Typography
                      variant="h5"
                      gutterBottom
                      sx={{ color: "white", fontWeight: "bold" }}
                    >
                      {t("landingPage.physioUserLogin")}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ color: "rgba(255, 255, 255, 0.8)" }}
                    >
                      {t("landingPage.physioUserDescription")}
                    </Typography>
                  </CardContent>
                  <CardActions sx={{ justifyContent: "center", pb: 3 }}>
                    <Button
                      variant="contained"
                      size="large"
                      sx={{
                        backgroundColor: "#F59E0B",
                        color: "white",
                        px: 4,
                        py: 1.5,
                        fontSize: "1.1rem",
                        fontWeight: "bold",
                        "&:hover": {
                          backgroundColor: "#D97706",
                        },
                      }}
                    >
                      {t("landingPage.continueAsPhysioUser")}
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            </Grid>

            {/* Footer Text */}
            <Box sx={{ textAlign: "center", mt: 6 }}>
              <Typography
                variant="body2"
                sx={{ color: "rgba(255, 255, 255, 0.6)", whiteSpace: "nowrap" }}
              >
                <Trans
                  i18nKey="landingPage.notSureWhichOption"
                  components={{
                    email: (
                      <a
                        href="mailto:info@deskdynamics.com"
                        style={{ color: "inherit", textDecoration: "underline" }}
                      />
                    ),
                  }}
                />
              </Typography>
            </Box>
          </Container>
        );
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <Drawer
        anchor="right"
        open={mobileDrawerOpen}
        onClose={() => setMobileDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: 280,
            backgroundColor: "#00003e",
            color: "white",
            paddingTop: "80px",
          },
        }}
      >
        <List>
          {currentPage !== "home" && (
            <ListItem
              button
              onClick={() => {
                setCurrentPage("home");
                setMobileDrawerOpen(false);
              }}
            >
              <ArrowBackIcon />
            </ListItem>
          )}

          <ListItem
            button
            onClick={() => {
              setCurrentPage("about");
              setMobileDrawerOpen(false);
            }}
          >
            <ListItemText primary={t("landingPage.aboutUs")} />
          </ListItem>

          <ListItem
            button
            onClick={() => {
              setCurrentPage("help");
              setMobileDrawerOpen(false);
            }}
          >
            <ListItemText primary={t("landingPage.helpFaq")} />
          </ListItem>

          <ListItem
            button
            onClick={() => {
              setCurrentPage("feedback");
              setMobileDrawerOpen(false);
            }}
          >
            <ListItemText primary={t("feedback")} />
          </ListItem>

          {!isMobileDevice() && !isElectron() && (
            <ListItem
              button
              onClick={(e) => {
                setDownloadMenuAnchor(e.currentTarget);
                setMobileDrawerOpen(false);
              }}
            >
              <ListItemText primary={t("landingPage.download")} />
            </ListItem>
          )}

          <ListItem
            button
            onClick={(e) => {
              setLanguageMenuAnchor(e.currentTarget);
            }}
          >
            <ListItemText primary={t("landingPage.language")} />
          </ListItem>

          <ListItem
            button
            onClick={(e) => {
              setFooterMenuAnchor(e.currentTarget);
            }}
          >
            <ListItemText primary={t("landingPage.legal")} />
          </ListItem>
        </List>
      </Drawer>

      {/* Header */}
      <AppBar position="fixed" sx={{ backgroundColor: "#00003e" }}>
        <Toolbar
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: "15px",
          }}
        >
          {/* Logo */}
          <a href={process.env.REACT_APP_FIREBASE_BASE_URL}>
            <img
              src={DynamicDeskLogo}
              style={{ width: "auto", height: "50px" }}
              alt="Dynamic Desk Logo"
            />
          </a>

          <Box sx={{ display: "flex", alignItems: "center" }}>
            {!isMobile && (
              <>
                {/* Header Navigation */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {currentPage !== "home" && (
                    <IconButton
                      color="inherit"
                      onClick={() => setCurrentPage("home")}
                      size="small"
                    >
                      <ArrowBackIcon />
                    </IconButton>
                  )}

                  <Typography
                    variant="body2"
                    onClick={() => setCurrentPage("about")}
                    sx={{
                      color: "white",
                      cursor: "pointer",
                      px: 1,
                      whiteSpace: "nowrap",
                      "&:hover": { opacity: 0.8 },
                    }}
                  >
                    {t("landingPage.aboutUs")}
                  </Typography>

                  <Typography
                    variant="body2"
                    onClick={() => setCurrentPage("help")}
                    sx={{
                      color: "white",
                      cursor: "pointer",
                      px: 1,
                      whiteSpace: "nowrap",
                      "&:hover": { opacity: 0.8 },
                    }}
                  >
                    {t("landingPage.helpFaq")}
                  </Typography>
                  <Typography
                    variant="body2"
                    onClick={() => setCurrentPage("feedback")}
                    sx={{
                      color: "white",
                      cursor: "pointer",
                      px: 1,
                      whiteSpace: "nowrap",
                      "&:hover": { opacity: 0.8 },
                    }}
                  >
                    {t("feedback")}
                  </Typography>
                </Box>
                {/* Download Dropdown - Only show if not on mobile and not in Electron */}
                {shouldShowDownloadButton() && (
                  <Typography
                    variant="body2"
                    onClick={(e) => setDownloadMenuAnchor(e.currentTarget)}
                    sx={{
                      color: "white",
                      cursor: "pointer",
                      px: 1,
                      whiteSpace: "nowrap",
                      "&:hover": { opacity: 0.8 },
                    }}
                  >
                    {t("landingPage.download")}
                  </Typography>
                )}
                <IconButton
                  color="inherit"
                  onClick={(e) => setLanguageMenuAnchor(e.currentTarget)}
                >
                  <LanguageIcon />
                </IconButton>
                <IconButton
                  color="inherit"
                  onClick={(e) => setFooterMenuAnchor(e.currentTarget)}
                >
                  <PolicyIcon />
                </IconButton>
              </>
            )}{" "}
            {isMobile && (
              <IconButton
                color="inherit"
                onClick={() => setMobileDrawerOpen(true)}
              >
                <MenuIcon />
              </IconButton>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Language Menu */}
      <Menu
        anchorEl={languageMenuAnchor}
        open={Boolean(languageMenuAnchor)}
        onClose={() => setLanguageMenuAnchor(null)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        disablePortal
      >
        <MenuItem onClick={() => handleLanguageChange("en")}>
          {t("landingPage.english")}
        </MenuItem>
        <MenuItem onClick={() => handleLanguageChange("de")}>
          {t("landingPage.german")}
        </MenuItem>
      </Menu>

      {/* Footer Menu */}
      <Menu
        anchorEl={footerMenuAnchor}
        open={Boolean(footerMenuAnchor)}
        onClose={() => setFooterMenuAnchor(null)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        disablePortal
      >
        <MenuItem
          onClick={() => {
            setCurrentPage("privacy");
            setFooterMenuAnchor(null);
          }}
        >
          {t("landingPage.privacyPolicy")}
        </MenuItem>
        <MenuItem
          onClick={() => {
            setCurrentPage("terms");
            setFooterMenuAnchor(null);
          }}
        >
          {t("landingPage.termsOfService")}
        </MenuItem>
        <MenuItem
          onClick={() => {
            setCurrentPage("cookies");
            setFooterMenuAnchor(null);
          }}
        >
          {t("landingPage.cookiePolicy")}
        </MenuItem>
        <MenuItem
          onClick={() => {
            setCurrentPage("imprint");
            setFooterMenuAnchor(null);
          }}
        >
          {t("landingPage.imprint")}
        </MenuItem>
        <MenuItem
          onClick={() => {
            setCurrentPage("additional-terms");
            setFooterMenuAnchor(null);
          }}
        >
          {t("landingPage.additionalTerms")}
        </MenuItem>
      </Menu>

      {/* Download Menu - Only render if download button should be shown */}
      {shouldShowDownloadButton() && (
        <Menu
          anchorEl={downloadMenuAnchor}
          open={Boolean(downloadMenuAnchor)}
          onClose={() => setDownloadMenuAnchor(null)}
          PaperProps={{
            sx: {
              minWidth: 200,
              mt: 1,
            },
          }}
        >
          <MenuItem disabled sx={{ opacity: 1, py: 1 }}>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: "bold", color: "text.primary" }}
            >
              {t("landingPage.downloadDesktopApp")}
            </Typography>
          </MenuItem>
          <MenuItem disabled sx={{ opacity: 1, py: 0 }}>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {t("landingPage.downloadDesktopDescription")}
            </Typography>
          </MenuItem>
          <MenuItem
            onClick={() => handleDownload()}
            disabled={isDownloading}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mt: 1,
              backgroundColor: isDownloading ? "action.hover" : "transparent",
              "&:hover": {
                backgroundColor: "action.hover",
              },
            }}
          >
            <Typography>
              {isDownloading ? t("landingPage.downloading") : ""}
            </Typography>
          </MenuItem>
          {availablePlatforms.map((platform) => (
            <MenuItem
              key={platform.key}
              onClick={() => handleDownload(platform.key)}
              disabled={isDownloading}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                "&:hover": {
                  backgroundColor: "action.hover",
                },
              }}
            >
              <span style={{ fontSize: "1.2em" }}>{platform.icon}</span>
              <Typography>{platform.displayName}</Typography>
            </MenuItem>
          ))}
        </Menu>
      )}

      {/* Main Content */}
      <Box component="main" sx={{ flexGrow: 1 }}>
        {renderCurrentPage()}
      </Box>

      {/* Footer */}
      {currentPage === "home" && (
        <Box
          component="footer"
          sx={{
            py: 3,
            px: 2,
            backgroundColor: "rgba(0, 0, 0, 0.3)",
            borderTop: "1px solid rgba(255, 255, 255, 0.1)",
          }}
        >
          <Container maxWidth="lg">
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: 3,
                flexWrap: "wrap",
              }}
            >
              <Typography
                variant="body2"
                onClick={() => setCurrentPage("privacy")}
                sx={{
                  color: "rgba(255, 255, 255, 0.7)",
                  cursor: "pointer",
                  "&:hover": { color: "white" },
                }}
              >
                {t("landingPage.privacyPolicy")}
              </Typography>

              <Typography
                variant="body2"
                onClick={() => setCurrentPage("terms")}
                sx={{
                  color: "rgba(255, 255, 255, 0.7)",
                  cursor: "pointer",
                  "&:hover": { color: "white" },
                }}
              >
                {t("landingPage.termsOfService")}
              </Typography>

              <Typography
                variant="body2"
                onClick={() => setCurrentPage("cookies")}
                sx={{
                  color: "rgba(255, 255, 255, 0.7)",
                  cursor: "pointer",
                  "&:hover": { color: "white" },
                }}
              >
                {t("landingPage.cookiePolicy")}
              </Typography>

              <Typography
                variant="body2"
                onClick={() => setCurrentPage("imprint")}
                sx={{
                  color: "rgba(255, 255, 255, 0.7)",
                  cursor: "pointer",
                  "&:hover": { color: "white" },
                }}
              >
                {t("landingPage.imprint")}
              </Typography>

              <Typography
                variant="body2"
                onClick={() => setCurrentPage("additional-terms")}
                sx={{
                  color: "rgba(255, 255, 255, 0.7)",
                  cursor: "pointer",
                  "&:hover": { color: "white" },
                }}
              >
                {t("landingPage.additionalTerms")}
              </Typography>
            </Box>
            <Typography
              variant="body2"
              align="center"
              sx={{ color: "rgba(255, 255, 255, 0.5)", mt: 2 }}
            >
              © 2025 Desk Dynamics. {t("landingPage.allRightsReserved")}
            </Typography>
          </Container>
        </Box>
      )}

      {/* Download Success/Error Snackbars */}
      <Snackbar
        open={downloadSuccess}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="success"
          sx={{ width: "100%" }}
        >
          {t("landingPage.downloadStarted")}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!downloadError}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="error"
          sx={{ width: "100%" }}
        >
          {downloadError}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LandingPage;
